const MAXIMUM_TRY = 1000;

class DocumentMatcher {
  constructor() {
    this.proposals = [];
    this.currentIndex = -1;
    this.documents = [];
    this.transaction = null;
  }

  loadData(transaction, documents) {
    this.transaction = transaction;
    this.documents = documents;
    this.proposals = this._findProposals();
    this.currentIndex = -1;
  }

  propose() {
    if (this.proposals.length === 0) {
      return { documents: [], index: -1, total: 0 };
    }

    this.currentIndex = (this.currentIndex + 1) % this.proposals.length;

    return {
      documents: this.proposals[this.currentIndex],
      index: this.currentIndex,
      total: this.proposals.length,
    };
  }

  // A transaction can consolidate several documents (e.g. paying two invoices at once, or an
  // invoice reduced by a credit note), and those documents don't all pull in the same direction:
  // a "creditor" document reduces the amount owed while a "debitor" document increases it, the
  // same way a "debit" transaction is the mirror image of a "credit" one. A proposal is therefore
  // a combination of documents whose *signed* amounts sum up to the transaction's *signed* amount
  // - mirroring the balance check the server performs in TransactionSplitAmountService.
  _findProposals() {
    const proposals = [];
    const target = this._getSignedAmount(this.transaction.type, this.transaction.amount);
    const signedAmounts = this.documents.map((document) => this._getSignedAmount(document.type, document.totalAmount));

    // The smallest/largest signed sum still reachable using documents[i:], so branches that could
    // never reach the target - regardless of which of the remaining documents are picked - can be
    // pruned immediately instead of enumerating them.
    const minReachable = new Array(signedAmounts.length + 1).fill(0);
    const maxReachable = new Array(signedAmounts.length + 1).fill(0);
    for (let i = signedAmounts.length - 1; i >= 0; i--) {
      minReachable[i] = minReachable[i + 1] + Math.min(0, signedAmounts[i]);
      maxReachable[i] = maxReachable[i + 1] + Math.max(0, signedAmounts[i]);
    }

    let tries = 0;

    const search = (startIndex, currentSum, currentProposal) => {
      if (currentProposal.length >= 2 && currentSum === target) {
        proposals.push(currentProposal.slice());
        return;
      }

      if (currentSum + minReachable[startIndex] > target || currentSum + maxReachable[startIndex] < target) {
        return;
      }

      for (let i = startIndex; i < this.documents.length; i++) {
        if (tries >= MAXIMUM_TRY) {
          return;
        }
        tries++;

        currentProposal.push(this.documents[i]);
        search(i + 1, currentSum + signedAmounts[i], currentProposal);
        currentProposal.pop();
      }
    };

    search(0, 0, []);

    return proposals;
  }

  _getSignedAmount(type, amount) {
    if (type === 'debitor' || type === 'credit') {
      return amount;
    }
    if (type === 'creditor' || type === 'debit') {
      return -amount;
    }
    return 0;
  }
}

export default DocumentMatcher;
